'use client';

import { useEffect, useState } from 'react';

import { isAndroidBrowser } from '../utils/is-android-browser';
import { maxClearance } from '../utils/maxClearance';
import { useEchoLauncherClearance } from './useEchoLauncherReserve';
import { useIsAppUser } from './useIsAppUser';

/**
 * Height in px a page should reserve at the end of its scroll container so its
 * last content can clear the mobile bottom tab bar.
 *
 * The dashboard's `TabNav` is `position: fixed; bottom: 0`, so it OVERLAYS page
 * content instead of shrinking it, and `(dashboard)/layout.tsx` deliberately
 * adds no bottom padding ("pages own their own scroll container with last-item
 * breathing room"). A page whose scroller runs the full viewport height
 * therefore keeps its final elements permanently underneath the bar — they can't
 * be scrolled into view because there's nothing below them to scroll to.
 *
 * Measured rather than hardcoded, for two reasons:
 *   - The bar's height is content-driven (icon + label + `py-2`), so the
 *     assorted `56px`/`58px`/`60px`/`64px` literals elsewhere in the codebase
 *     are guesses that drift from the real thing.
 *   - Its rendered height ALREADY includes the platform safe-area padding. The
 *     bar itself now reads `var(--safe-area-inset-bottom)`, which the shell
 *     publishes a real value for on Android; the per-page literals elsewhere
 *     still spell it `env(safe-area-inset-bottom)`, which reads 0 in that
 *     WebView — so those under-reserve on Android, which is exactly where the
 *     clipping was reported.
 *
 * Measured from the VIEWPORT BOTTOM to the bar's top edge, not as the bar's own
 * height: echo creators get `EchoTabNav`, a floating capsule that sits
 * `max(1.5rem, var(--safe-area-inset-bottom))` ABOVE the bottom edge, so its
 * height alone leaves the last content underneath it — and that float is
 * device-dependent (~34pt on an iPhone with a home indicator, a 48dp nav bar on
 * 3-button Android), which is exactly why a per-page literal can clear it on
 * one device and not the next.
 * For the full-width `TabNav`, flush at `bottom: 0`, the two are identical.
 *
 * Returns 0 wherever the bar isn't rendered — on `lg+` (it's `lg:hidden`, so
 * `offsetHeight` is 0) and on hosts outside the `(dashboard)` layout (the public
 * bio, the AI profile builder) — so callers need no host-specific branching.
 *
 * Matches on `nav[...]`, which is unambiguous: the desktop sidebar carries the
 * same `aria-label` but is an `<aside>`.
 */
export function useBottomNavReserve(): number {
  const [reserve, setReserve] = useState(0);

  useEffect(() => {
    const measure = () => {
      const bar = document.querySelector<HTMLElement>(
        'nav[aria-label="Dashboard navigation"]',
      );
      // `offsetHeight` 0 means `display: none` (`lg:hidden`), where
      // `getBoundingClientRect()` is all-zeros and would read as a
      // full-viewport reserve.
      if (!bar || bar.offsetHeight === 0) {
        setReserve(0);
        return;
      }
      // `documentElement.clientHeight`, NOT `window.innerHeight`. The bar is
      // `position: fixed`, so `getBoundingClientRect()` reports it in LAYOUT
      // viewport coordinates; on iOS `innerHeight` is the VISUAL viewport, which
      // diverges from that whenever the URL bar collapses, the keyboard opens or
      // the page is pinch-zoomed. Subtracting the two mismatched spaces is what
      // made the reserve come out right on Android (where they agree) and short
      // on iOS / mobile web.
      const viewportBottom = document.documentElement.clientHeight;
      setReserve(Math.max(0, viewportBottom - bar.getBoundingClientRect().top));
    };

    let barObserver: ResizeObserver | undefined;

    // Measure, and once the bar exists start watching it — `ResizeObserver`
    // fires immediately on `observe`, so this also covers the post-paint pass.
    // Returns whether the bar was found.
    const sync = () => {
      measure();
      const bar = document.querySelector<HTMLElement>(
        'nav[aria-label="Dashboard navigation"]',
      );
      if (bar && !barObserver) {
        barObserver = new ResizeObserver(measure);
        barObserver.observe(bar);
      }
      return bar != null;
    };

    // The `@nav` parallel slot suspends behind `DashboardNavSkeleton`, which
    // draws no bottom bar —
    // so until the slot's server gate resolves there is no bar here to measure
    // at all. Watch for it to land instead of guessing a timeout: a fixed delay
    // is a race the browser loses on a cold dev-server compile, and losing it
    // pins the reserve at 0 forever (the page then silently keeps its stale
    // hardcoded padding, which is the "still overlaps on web" report).
    let slotObserver: MutationObserver | undefined;
    if (!sync()) {
      slotObserver = new MutationObserver(() => {
        if (sync()) {
          slotObserver?.disconnect();
          slotObserver = undefined;
        }
      });
      slotObserver.observe(document.body, { childList: true, subtree: true });
    }

    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    // iOS doesn't fire `window.resize` when the soft keyboard opens, and the
    // lead-center report is only wrong once the search field takes focus — so
    // listen on the visual viewport too.
    window.visualViewport?.addEventListener('resize', measure);

    return () => {
      barObserver?.disconnect();
      slotObserver?.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      window.visualViewport?.removeEventListener('resize', measure);
    };
  }, []);

  return reserve;
}

/** Breathing room between a page's last row and the bar/capsule above it. */
const BOTTOM_NAV_CLEARANCE_PX = 16;

/**
 * How far above the viewport bottom a page's bottom-anchored content has to sit
 * to clear the mobile bottom nav. In px.
 *
 * Apply to whichever property anchors the element; `undefined` makes React skip
 * the inline value entirely, so the page's own Tailwind class stays in charge:
 *
 * ```tsx
 * // scrolling list — push the last row up
 * <div className="… pb-24 sm:pb-6" style={{ paddingBottom: clearance }}>
 * // sticky/fixed action button — lift the whole button
 * <div className="sticky bottom-[3.5rem] …" style={{ bottom: clearance }}>
 * ```
 *
 * Pages hardcode `pb-24` / `bottom-[3.5rem]`-style reserves that were calibrated
 * against the ORIGINAL full-width `TabNav`. Echo creators now get `EchoTabNav` —
 * a capsule that floats 1.5rem above the bottom edge — so its real footprint is
 * taller than those literals, and the last row (the "Showing N of M" counter,
 * the Download CSV button) ends up on top of it. {@link useBottomNavReserve}
 * measures the actual footprint, so this tracks whichever bar rendered instead
 * of re-guessing it per page.
 *
 * NOT app-gated: both bars are `lg:hidden`, so they render on EVERY mobile
 * viewport and the overlap is identical on the plain mobile web. The measurement
 * is the only gate needed — `undefined` on `lg+` and outside the dashboard
 * layout, where no bar exists and the page's own offsets stay in charge.
 */
export function useBottomNavClearance(): number | undefined {
  const reserve = useBottomNavReserve();

  // `reserve === 0` means no bar is rendered (desktop, or a host outside the
  // dashboard layout) — leave the page's own offsets alone.
  if (reserve === 0) return undefined;
  return reserve + BOTTOM_NAV_CLEARANCE_PX;
}

/** Tailwind's `lg` breakpoint, below which the bottom bars render (`lg:hidden`). */
const BELOW_LG_QUERY = '(max-width: 1023.98px)';

/**
 * True below `lg` — the widths where the floating ECHO head shares the bottom
 * corner with a page's last row. Tracks the query live, so a resize across the
 * breakpoint re-decides.
 */
function useIsBelowLg(): boolean {
  const [belowLg, setBelowLg] = useState(false);
  useEffect(() => {
    const query = window.matchMedia(BELOW_LG_QUERY);
    setBelowLg(query.matches);
    const onChange = (event: MediaQueryListEvent) => setBelowLg(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return belowLg;
}

/**
 * Bottom clearance for a page whose route HIDES the mobile bottom bar
 * (`kTabNavHiddenRoutes` in `@nav/tab/index.tsx`).
 *
 * Hiding the bar doesn't make the bottom edge safe, so a hidden-nav page can't
 * just drop its reserve to nothing:
 *   - a bar still renders (mobile web, until that flag flips) → clear it, exactly
 *     as {@link useBottomNavClearance} does;
 *   - our bar is gone but the Android app's WebView still runs under the device's
 *     system nav bar → clear that instead ({@link useAndroidSystemNavInset});
 *   - neither applies (desktop, iOS, Android gesture nav) → `undefined`, so the
 *     page's own class stays in charge.
 *
 * One hook and one inline value per page, same shape as the bar-only case. Pages
 * pair it with a class carrying `env(safe-area-inset-bottom)`, which is what
 * covers iOS while this returns `undefined` there.
 */
export function useHiddenNavBottomClearance(): number | undefined {
  const navClearance = useBottomNavClearance();
  const androidInset = useAndroidSystemNavInset();
  const belowLg = useIsBelowLg();
  // Measured only below `lg`, where it is used: the launcher's observer reflows
  // on every DOM class/style change, a cost a desktop page would pay for a value
  // it throws away.
  const echoClearance = useEchoLauncherClearance(belowLg);

  const barClearance =
    navClearance ??
    (androidInset ? androidInset + BOTTOM_NAV_CLEARANCE_PX : undefined);

  // The floating ECHO head overlays the SAME corner as the bar and sits higher,
  // so the reserve is whichever is taller — folded in here rather than at each
  // call site, since every page that clears the bar has the identical problem
  // with the head.
  //
  // Gated on the WIDTH, not on a bar being present: a hidden-nav route on a
  // phone (iOS app, mobile web) has no bar at all, yet the head still floats
  // over its last row. Desktop stays out — the head sits in the corner of a
  // wide page, nowhere near a row's own controls, and reserving for it there
  // only put an empty band under every table.
  return maxClearance(barClearance, echoClearance);
}

/**
 * How much of the bottom of the Android app's WebView sits BEHIND the device's
 * own system navigation bar, in px. 0 everywhere else.
 *
 * The native shell runs EDGE-TO-EDGE and reserves nothing, so the WebView
 * extends the full height of the window — under a 48dp 3-button / 2-button nav
 * bar in its entirety. Gesture-nav Android leaves only a few px, and iOS
 * reports a real inset so it's unaffected.
 *
 * Only needed by content anchored to the VIEWPORT bottom on a full-screen
 * takeover (`position: fixed` overlays), which the mobile bottom nav doesn't
 * cover — inside the dashboard layout {@link useBottomNavClearance} already
 * clears far more than this. `env(safe-area-inset-bottom)` is no help: it reads
 * 0 inside the Android WebView, which is exactly the failing case, so this is a
 * flat value rather than a measurement. Nav mode isn't detectable from the web,
 * so the worst case (3-button) is reserved for every Android app user; on
 * gesture nav it just reads as slightly more breathing room.
 *
 * PREFER `var(--safe-area-inset-bottom)` in CSS where the value can be
 * expressed there: the shell now publishes its real measured inset over that
 * variable on Android (`src/helpers/safeAreaBridge.ts` in PopshopMobile), so it
 * is exact rather than worst-case, and it needs no app or user-agent gate. This
 * hook remains for the callers that need the figure as a JS NUMBER for an
 * inline style; migrating them is a separate change.
 */
export function useAndroidSystemNavInset(): number {
  const isAppUser = useIsAppUser();
  // `useIsAppUser` is `false` for the server render AND the hydration render,
  // so the user-agent sniff is short-circuited away on both — no mismatch.
  return isAppUser && isAndroidBrowser() ? ANDROID_SYSTEM_NAV_INSET_PX : 0;
}

/**
 * A 48dp 3-button system nav bar, in full: the edge-to-edge shell reserves none
 * of it. This was 28 while the shell padded the WebView by a flat 20px.
 */
const ANDROID_SYSTEM_NAV_INSET_PX = 48;
